import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    LoadingOutlined,
} from '@ant-design/icons'

import { Button, Modal } from '@ui/index'
import type { ContractDocumentSendFileStatus } from '@api/contracts/state.types'

import type { ICardContractSendProgress } from './types'
import css from './ui.module.scss'


const statusLabels: Record<ContractDocumentSendFileStatus, string> = {
    pending: 'Ожидает',
    sending: 'Отправляется',
    sent: 'Отправлен',
    failed: 'Не отправлен',
}

const getStatusIcon = (status: ContractDocumentSendFileStatus) => {
    switch (status) {
        case 'sending': return <LoadingOutlined spin />
        case 'sent': return <CheckCircleOutlined />
        case 'failed': return <CloseCircleOutlined />
        default: return <ClockCircleOutlined />
    }
}

export const CardContractSendProgress = ({isOpen, job, handlerButtonCancel}: ICardContractSendProgress) => {
    const progress = Math.min(100, Math.max(0, Math.floor(job.progress)))
    const isFinished = job.status === 'completed' || job.status === 'failed'
    const isFailed = job.status === 'failed'

    return <Modal
        isOpen={isOpen}
        isEsc={isFinished}
        onCancel={isFinished ? handlerButtonCancel : undefined}
        overflowY='auto'
        maxWidth='560px'
        zIndex='1080'
    >
        <div className={`modal_container ${css.modal}`} role='dialog' aria-modal='true' aria-labelledby='contract-send-progress-title'>
            <div className='modal_container-header'>
                <h4 id='contract-send-progress-title'>Отправка документов на подпись</h4>
            </div>

            <div className={`modal_container-body ${css.body}`} aria-live='polite'>
                <div className={css.summary}>
                    <p>{job.message}</p>
                    <strong className={isFailed ? css.failedText : undefined}>{progress}%</strong>
                </div>

                <div
                    className={css.progressTrack}
                    role='progressbar'
                    aria-label='Прогресс отправки документов'
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                    aria-valuetext={job.message}
                >
                    <span
                        className={`${css.progressIndicator} ${isFailed ? css.progressFailed : ''}`.trim()}
                        style={{width: `${progress}%`}}
                    />
                </div>

                <p className={css.counters}>
                    {job.totalFiles > 0
                        ? `Обработано: ${job.processedFiles} из ${job.totalFiles}. Отправлено: ${job.sentFiles}. Ошибок: ${job.failedFiles}.`
                        : 'Формируется список файлов.'
                    }
                </p>

                {job.files.length > 0
                    ? <ul className={css.fileList} aria-label='Результаты отправки файлов'>
                        {job.files.map((file) => <li key={file.fileID} className={`${css.fileRow} ${css[file.status]}`}>
                            <span className={css.statusIcon} aria-hidden='true'>{getStatusIcon(file.status)}</span>
                            <span className={css.fileDetails}>
                                <strong>{file.fileName}</strong>
                                {file.errorMessage && <small>{file.errorMessage}</small>}
                            </span>
                            <span className={css.statusLabel}>{statusLabels[file.status]}</span>
                        </li>)}
                    </ul>
                    : <div className={css.preparing}>
                        <LoadingOutlined spin aria-hidden='true' />
                        <span>Подготовка данных для отправки</span>
                    </div>
                }

                {job.errorMessage && <div className={css.jobError} role='alert'>{job.errorMessage}</div>}

                {isFinished && <div className={css.actions}>
                    <Button width='100%' onClick={handlerButtonCancel}>Закрыть</Button>
                </div>}
            </div>
        </div>
    </Modal>
}